import React, { useState, useEffect } from 'react';
import { LicenseInfo } from '../services/LicenseService';

interface GenerateProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (config: ProjectGenerationConfig) => void;
  licenseInfo: LicenseInfo | null;
  documentName: string;
  isGenerating: boolean;
}

export interface ProjectGenerationConfig {
  projectName: string;
  flattenedPdfAllocation: number;
  comments: string;
}

type DialogStep = 'configure' | 'confirm';

const GenerateProjectDialog: React.FC<GenerateProjectDialogProps> = ({
  isOpen,
  onClose,
  onGenerate,
  licenseInfo,
  documentName,
  isGenerating,
}) => {
  const [step, setStep] = useState<DialogStep>('configure');
  const [projectName, setProjectName] = useState('');
  const [allocation, setAllocation] = useState(0);
  const [comments, setComments] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Derive allocation limits from license info
  const tierLimit = licenseInfo?.flattenedPdfs?.limit ?? 0;
  const alreadyAllocated = licenseInfo?.flattenedPdfs?.allocated ?? 0;
  const available = Math.max(0, tierLimit - alreadyAllocated);

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setStep('configure');
      let name = documentName.replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9_-]/g, '_');
      if (/^\d/.test(name)) {
        name = 'Form_' + name;
      }
      setProjectName(name);
      setAllocation(0);
      setComments('');
      setError(null);
    }
  }, [isOpen, documentName]);

  if (!isOpen) return null;

  const handleReviewSummary = () => {
    setError(null);

    if (!projectName.trim()) {
      setError('Project name is required');
      return;
    }

    if (/^\d/.test(projectName.trim())) {
      setError('Project name must start with a letter');
      return;
    }

    if (allocation <= 0) {
      setError('Flattened PDF allocation must be greater than 0');
      return;
    }

    if (allocation > available) {
      setError(`Allocation cannot exceed available amount (${available.toLocaleString()})`);
      return;
    }

    setStep('confirm');
  };

  const handleGenerate = () => {
    onGenerate({
      projectName: projectName.trim(),
      flattenedPdfAllocation: allocation,
      comments: comments.trim(),
    });
  };

  const remainingAfter = Math.max(0, available - allocation);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-emerald-50 to-emerald-100/50 border-b border-emerald-200 flex-shrink-0">
          <h2 className="text-lg font-display font-semibold text-neutral-900">
            {step === 'configure' ? 'Generate Spring Boot Project' : 'Confirm Project Generation'}
          </h2>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {step === 'configure' ? (
            /* ── Configuration Step ── */
            <div className="space-y-5">
              {/* License & Tier Info (read-only) */}
              <div className="bg-neutral-50 rounded-lg p-4 border border-neutral-200">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-neutral-500">Tier:</span>{' '}
                    <span className="font-medium text-neutral-900">
                      {licenseInfo?.tier ?? 'Unknown'} ({licenseInfo?.periodType ?? ''})
                    </span>
                  </div>
                  <div>
                    <span className="text-neutral-500">Code Generation:</span>{' '}
                    <span className="font-medium text-emerald-700">
                      {licenseInfo?.codeGeneration?.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
              </div>

              {/* PDF Template */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">PDF Template</label>
                <div className="text-sm text-neutral-600 bg-neutral-50 rounded-lg px-3 py-2 border border-neutral-200">
                  {documentName}
                </div>
              </div>

              {/* Flattened PDF Allocation */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h3 className="text-sm font-semibold text-neutral-900 mb-3">Flattened PDF Allocation</h3>
                <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                  <div>
                    <span className="text-neutral-500">Tier Limit:</span>{' '}
                    <span className="font-medium">
                      {tierLimit.toLocaleString()} / {licenseInfo?.periodType?.toLowerCase() ?? 'month'}
                    </span>
                  </div>
                  <div>
                    <span className="text-neutral-500">Already Allocated:</span>{' '}
                    <span className="font-medium">{alreadyAllocated.toLocaleString()}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-neutral-500">Available:</span>{' '}
                    <span className="font-semibold text-emerald-700">
                      {available.toLocaleString()}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Allocate to this project
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={available}
                    value={allocation || ''}
                    onChange={(e) => {
                      setAllocation(parseInt(e.target.value) || 0);
                      setError(null);
                    }}
                    placeholder="Enter allocation amount"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                  <p className="text-xs text-neutral-600 mt-1">
                    How many flattened PDFs this project can produce. This amount is
                    subtracted from your available count when you generate.
                  </p>
                </div>

                {allocation > 0 && (
                  <div className="mt-2 text-sm">
                    <span className="text-neutral-500">Remaining after allocation:</span>{' '}
                    <span className={`font-medium ${remainingAfter < tierLimit * 0.1 ? 'text-amber-600' : 'text-neutral-700'}`}>
                      {remainingAfter.toLocaleString()}
                    </span>
                  </div>
                )}
              </div>

              {/* Allocation tip */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-xs text-blue-800">
                Use Live Form Preview to test and adjust fields on a single PDF before generating the project with a fixed allocation.
              </div>

              {/* Project Details */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Project Name</label>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="e.g., HR_Onboarding_Forms"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
                <p className="text-xs text-neutral-500 mt-1">Must start with a letter. Used for generated class and file names.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Comments <span className="text-neutral-400">(optional)</span>
                </label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="e.g., Production deployment for Q2 intake"
                  rows={2}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                />
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-sm text-red-700">
                  {error}
                </div>
              )}
            </div>
          ) : (
            /* ── Confirmation Step ── */
            <div className="space-y-4">
              <div className="bg-neutral-50 rounded-lg p-4 border border-neutral-200 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-neutral-500">Project:</span>
                  <span className="font-medium text-neutral-900">{projectName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">PDF Template:</span>
                  <span className="font-medium text-neutral-900">{documentName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">Tier:</span>
                  <span className="font-medium text-neutral-900">
                    {licenseInfo?.tier} ({licenseInfo?.periodType})
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">Flattened PDF Allocation:</span>
                  <span className="font-semibold text-emerald-700">
                    {allocation.toLocaleString()}
                  </span>
                </div>
                {comments && (
                  <div className="pt-2 border-t border-neutral-200">
                    <span className="text-neutral-500">Comments:</span>
                    <p className="text-neutral-700 mt-1">{comments}</p>
                  </div>
                )}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                <>
                  Generating uses <strong>{allocation.toLocaleString()}</strong> of your{' '}
                  <strong>{available.toLocaleString()}</strong> available flattened PDFs,
                  leaving <strong>{remainingAfter.toLocaleString()}</strong> for future projects.
                  The generated project can then produce up to {allocation.toLocaleString()} flattened
                  PDF{allocation === 1 ? '' : 's'} on its own — this number cannot be changed after generation.
                </>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-neutral-50 border-t border-neutral-200 flex justify-between flex-shrink-0">
          {step === 'configure' ? (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReviewSummary}
                className="px-6 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Review Summary
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setStep('configure')}
                disabled={isGenerating}
                className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 disabled:opacity-50"
              >
                Back to Edit
              </button>
              <button
                onClick={handleGenerate}
                disabled={isGenerating}
                className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-lg hover:from-emerald-700 hover:to-emerald-800 transition-all disabled:opacity-50 flex items-center"
              >
                {isGenerating ? (
                  <>
                    <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Generating...
                  </>
                ) : (
                  'Generate Project'
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerateProjectDialog;
